import { useCallback, useEffect, useState } from "react";

// RN-PROST (6010) only: which Размерување columns the user chose to hide, persisted
// per browser (not per user/server) — same "nice to have, never critical" tier as
// ColumnManager's own panel position (client/src/components/ColumnManager.tsx). A
// distinct storage key keeps this independent of every other table's column state.
const STORAGE_KEY = "rnSimpleRazmerHiddenColumns";

const loadHiddenColumns = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((key) => typeof key === "string") : [];
  } catch {
    return [];
  }
};

export interface UseRazmerHiddenColumnsResult {
  hiddenColumns: string[];
  handleColumnToggle: (columnKey: string) => void;
  handleColumnDrop: (columnKey: string) => void;
}

/**
 * State + persistence for the Размерување Column Manager on /work-order/6010
 * (RN-PROST). Kept out of OSRazmerTable/OSStagePanel so those stay presentational —
 * this hook owns the localStorage read/write, the components just render props.
 */
export const useRazmerHiddenColumns = (): UseRazmerHiddenColumnsResult => {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(loadHiddenColumns);

  useEffect(() => {
    try {
      if (hiddenColumns.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(hiddenColumns));
      }
    } catch {
      // localStorage unavailable (private mode / blocked) — visibility just won't persist.
    }
  }, [hiddenColumns]);

  const handleColumnToggle = useCallback((columnKey: string) => {
    setHiddenColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((key) => key !== columnKey) : [...prev, columnKey]
    );
  }, []);

  const handleColumnDrop = useCallback((columnKey: string) => {
    setHiddenColumns((prev) => (prev.includes(columnKey) ? prev : [...prev, columnKey]));
  }, []);

  return { hiddenColumns, handleColumnToggle, handleColumnDrop };
};

export default useRazmerHiddenColumns;
